import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Button, Menu, MenuItem, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, type LinkProps } from "react-router-dom";
import { useHasPermission } from "../../context/userContext";

type To = LinkProps["to"];

export interface NavbarMenuItemConfig {
	label: string;
	to: To;
	permission?: string;
}

export interface NavbarMenuConfig {
	id: string;
	label: string;
	to?: To;
	items: NavbarMenuItemConfig[];
	permission?: string;
}

export interface NavbarRendererProps {
	isMobile?: boolean;
	closeDrawer?: () => void;
}

interface VisibleNavbarMenuConfig extends NavbarMenuConfig {
	items: NavbarMenuItemConfig[];
}

interface NavbarMenuBarProps extends NavbarRendererProps {
	menus: NavbarMenuConfig[];
	hoverSwitchDelayMs?: number;
}

type NavTriggerElement = HTMLElement;

export function NavbarMenuBar({
	menus,
	isMobile = false,
	closeDrawer,
	hoverSwitchDelayMs = 10,
}: NavbarMenuBarProps) {
	const hasPermissionTo = useHasPermission();
	const [openMenuId, setOpenMenuId] = useState<string | null>(null);
	const [menuAnchorEl, setMenuAnchorEl] = useState<NavTriggerElement | null>(
		null,
	);
	const triggerRefs = useRef<Record<string, NavTriggerElement | null>>({});
	const hoverTimeoutRef = useRef<number | null>(null);

	const visibleMenus = useMemo<VisibleNavbarMenuConfig[]>(() => {
		return menus
			.filter((menu) => !menu.permission || hasPermissionTo(menu.permission))
			.map((menu) => ({
				...menu,
				items: menu.items.filter(
					(item) => !item.permission || hasPermissionTo(item.permission),
				),
			}))
			.filter((menu) => menu.items.length > 0 || Boolean(menu.to));
	}, [hasPermissionTo, menus]);

	const clearHoverTimeout = () => {
		if (hoverTimeoutRef.current !== null) {
			window.clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = null;
		}
	};

	const focusTrigger = (menuId: string) => {
		triggerRefs.current[menuId]?.focus();
	};

	const getAdjacentMenu = (menuId: string, direction: 1 | -1) => {
		const currentIndex = visibleMenus.findIndex((menu) => menu.id === menuId);

		if (currentIndex === -1) {
			return null;
		}

		const nextIndex =
			(currentIndex + direction + visibleMenus.length) % visibleMenus.length;

		return visibleMenus[nextIndex] ?? null;
	};

	const openMenu = (menuId: string, anchorEl?: NavTriggerElement | null) => {
		clearHoverTimeout();
		setOpenMenuId(menuId);
		setMenuAnchorEl(anchorEl ?? triggerRefs.current[menuId] ?? null);
	};

	const closeMenu = () => {
		clearHoverTimeout();
		setOpenMenuId(null);
		setMenuAnchorEl(null);
	};

	useEffect(() => () => clearHoverTimeout(), []);

	useEffect(() => {
		if (!openMenuId) {
			return;
		}

		const handlePointerDown = (event: MouseEvent | TouchEvent) => {
			const target = event.target as Node | null;
			const trigger = triggerRefs.current[openMenuId];
			const menuPaper = document.querySelector(
				`#${openMenuId}-menu .MuiPaper-root`,
			) as HTMLElement | null;

			if (
				target &&
				((trigger && trigger.contains(target)) ||
					(menuPaper && menuPaper.contains(target)))
			) {
				return;
			}

			closeMenu();
		};

		document.addEventListener("mousedown", handlePointerDown, true);
		document.addEventListener("touchstart", handlePointerDown, true);

		return () => {
			document.removeEventListener("mousedown", handlePointerDown, true);
			document.removeEventListener("touchstart", handlePointerDown, true);
		};
	}, [openMenuId]);

	const handleMenuOpen = (
		event: React.MouseEvent<HTMLElement>,
		menuId: string,
	) => {
		if (openMenuId === menuId) {
			closeMenu();
			return;
		}

		openMenu(menuId, event.currentTarget);
	};

	const handleMenuHover = (
		event: React.MouseEvent<HTMLElement>,
		menuId: string,
	) => {
		if (isMobile || !openMenuId || openMenuId === menuId) {
			return;
		}

		clearHoverTimeout();
		hoverTimeoutRef.current = window.setTimeout(() => {
			openMenu(menuId, event.currentTarget);
		}, hoverSwitchDelayMs);
	};

	const handleMenuKeyDown = (
		event: React.KeyboardEvent<HTMLElement>,
		menu: VisibleNavbarMenuConfig,
	) => {
		switch (event.key) {
			case "Enter":
			case " ":
			case "ArrowDown": {
				if (!menu.items.length) {
					return;
				}

				event.preventDefault();
				openMenu(menu.id, event.currentTarget);
				break;
			}
			case "ArrowRight":
			case "ArrowLeft": {
				event.preventDefault();
				const adjacentMenu = getAdjacentMenu(
					menu.id,
					event.key === "ArrowRight" ? 1 : -1,
				);

				if (!adjacentMenu) {
					return;
				}

				focusTrigger(adjacentMenu.id);

				if (openMenuId && adjacentMenu.items.length) {
					openMenu(adjacentMenu.id);
				}
				break;
			}
			case "Escape": {
				if (!openMenuId) {
					return;
				}

				event.preventDefault();
				closeMenu();
				focusTrigger(menu.id);
				break;
			}
		}
	};

	return (
		<>
			{visibleMenus.map((menu) => {
				const isMenuOpen = openMenuId === menu.id;
				const buttonId = `${menu.id}-trigger`;

				return menu.items.length > 0 ? (
					<React.Fragment key={menu.id}>
						<Button
							ref={(node: NavTriggerElement | null) => {
								triggerRefs.current[menu.id] = node;
							}}
							id={buttonId}
							aria-controls={`${menu.id}-menu`}
							aria-haspopup="true"
							aria-expanded={isMenuOpen ? "true" : undefined}
							onClick={(event) => handleMenuOpen(event, menu.id)}
							onMouseEnter={(event) => handleMenuHover(event, menu.id)}
							onMouseLeave={clearHoverTimeout}
							onKeyDown={(event) => handleMenuKeyDown(event, menu)}
							sx={{
								marginRight: (theme) => theme.spacing(2),
								color: "white",
								display: "flex",
								alignItems: "center",
								whiteSpace: "nowrap",
							}}
						>
							{menu.label}
							{isMenuOpen ? (
								<ExpandLess
									sx={{ marginLeft: (theme) => theme.spacing(0.5) }}
								/>
							) : (
								<ExpandMore
									sx={{ marginLeft: (theme) => theme.spacing(0.5) }}
								/>
							)}
						</Button>
						<Menu
							id={`${menu.id}-menu`}
							anchorEl={isMenuOpen ? menuAnchorEl : null}
							hideBackdrop
							disableScrollLock
							keepMounted
							open={isMenuOpen}
							onClose={closeMenu}
							sx={{
								pointerEvents: "none",
								"& .MuiPaper-root": {
									pointerEvents: "auto",
								},
							}}
							MenuListProps={{
								"aria-labelledby": buttonId,
								autoFocusItem: isMenuOpen,
							}}
							anchorOrigin={{
								vertical: "bottom",
								horizontal: "center",
							}}
							transformOrigin={{
								vertical: "top",
								horizontal: "center",
							}}
						>
							{menu.items.map((item, index) => (
								<MenuItem
									key={`${menu.id}-item-${item.label}-${index}`}
									component={Link}
									to={item.to}
									onClick={() => {
										closeMenu();
										closeDrawer?.();
									}}
									sx={{
										textDecoration: "none",
										color: "inherit",
									}}
								>
									<Typography>{item.label}</Typography>
								</MenuItem>
							))}
						</Menu>
					</React.Fragment>
				) : (
					<Button
						key={menu.id}
						ref={(node: NavTriggerElement | null) => {
							triggerRefs.current[menu.id] = node;
						}}
						id={buttonId}
						component={Link as React.ElementType}
						to={menu.to ?? "#"}
						onClick={() => {
							closeMenu();
							closeDrawer?.();
						}}
						onKeyDown={(event: React.KeyboardEvent<HTMLElement>) =>
							handleMenuKeyDown(event, menu)
						}
						sx={{
							marginRight: (theme) => theme.spacing(2),
							color: "white",
							display: "flex",
							alignItems: "center",
							whiteSpace: "nowrap",
						}}
					>
						{menu.label}
					</Button>
				);
			})}
		</>
	);
}
